import React, { useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { Message } from '@blink/components/src/constants/messages';
import { useDispatch, useSelector } from 'react-redux';

import { MarginAccountWithdrawalAmountField } from './WithdrawalAmountField';

import { OperationStatusType } from '@/features/Modals/ui/OperationStatusModal';
import { Store } from '@/shared/store';
import { useModal, useVaultMarginAccountWithdrawal } from '@/features/Modals/hooks';
import { ModalName } from '@/shared/store/modals/constants';
import { showModalAction } from '@/shared/store/modals/actions';
import { formatWithMaxPrecision } from '@/shared/utils/helpers';
import WithdrawalModalSchema, {
    WithdrawalModalData,
} from '@/shared/validations/withdrawalModal.schema';
import { Button, Text, TipCard, TooltipWrapper } from '@/shared/ui';
import { SummaryRow } from '@/widgets/Modals';

const WithdrawalModalTexts = {
    title: 'How much do you want to withdraw?',
    tip: 'Arkis Liquidity (LP) tokens automatically earn interest proportional to your share of the pool.',
    tooltips: {
        poolUtilization: 'Total token supply being used by borrowers for their operations',
        poolBalance: 'Pool Balance shows the available tokens that have not been used by borrowers',
    },
};

enum MAVaultsWithdrawalModalFields {
    AMOUNT = 'amount',
}

export const MarginAccountVaultsWithdrawalModal = () => {
    const { modalProps } = useModal();
    const dispatch = useDispatch();
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const {
        tokenSymbol,
        tokenAddress,
        totalPosition,
        totalDepositThreshold,
        decimals,
        poolUtilization,
        agreementAddress,
        marginAccountAddress,
        poolBalance,
        refresh,
        currentApy,
    } = modalProps || {};

    const methods = useForm<WithdrawalModalData>({
        resolver: yupResolver(WithdrawalModalSchema),
        reValidateMode: 'onChange',
    });

    const { makeWithdrawal } = useVaultMarginAccountWithdrawal();

    const currentModal = {
        activeModal: ModalName.MARGIN_ACCOUNTS_VAULT_WITHDRAWAL_MODAL,
        modalTitle: 'Supply Liquidity',
        modalProps,
    };

    const onSubmit = async (data: WithdrawalModalData) => {
        if (!walletAddress) {
            dispatch(
                showModalAction({
                    modalName: ModalName.CONNECT_WALLET_MODAL,
                    modalTitle: 'Connect Wallet',
                }),
            );
            return;
        }

        dispatch(
            showModalAction({
                modalName: ModalName.OPERATION_STATUS_MODAL,
                modalProps: {
                    type: OperationStatusType.Pending,
                    description: Message.PENDING_TEXT,
                },
                previousModal: currentModal,
            }),
        );

        await makeWithdrawal({
            amount: data[MAVaultsWithdrawalModalFields.AMOUNT],
            agreementAddress,
            marginAccountAddress,
            tokenAddress,
            totalPosition,
            poolBalance,
            decimals,
        });

        if (refresh) refresh();
    };

    const apy = Number(currentApy || '0').toFixed(2);

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className='w-[448px] pt-7'>
                <Text className='mb-3 mt-2'>{WithdrawalModalTexts.title}</Text>
                <MarginAccountWithdrawalAmountField name={MAVaultsWithdrawalModalFields.AMOUNT} />
                <div className='mt-5'>
                    <TipCard text={WithdrawalModalTexts.tip} title='Tip' />
                </div>
                <div className='border-b pb-7 mb-7 mt-5 border-border-opaque'>
                    <SummaryRow label='Current APY' value={`${apy} %`} />

                    <SummaryRow label='Pool Utilization' value={`${poolUtilization}%`}>
                        <TooltipWrapper text={WithdrawalModalTexts.tooltips.poolUtilization} />
                    </SummaryRow>

                    <SummaryRow label='Pool Cap' className={'border-none'}>
                        <div className='flex gap-2'>
                            <Text
                                type={Text.types.Paragraph}
                                fontWeight={Text.weights.SemiBold}
                            >{`${formatWithMaxPrecision(
                                poolBalance,
                                decimals,
                            )} ${tokenSymbol}`}</Text>
                            <Text
                                type={Text.types.Paragraph}
                                className='text-content-secondary'
                            >{`/ ${totalDepositThreshold} ${tokenSymbol}`}</Text>
                        </div>
                        <TooltipWrapper text={WithdrawalModalTexts.tooltips.poolBalance} />
                    </SummaryRow>
                </div>

                <SubmitButton />
            </form>
        </FormProvider>
    );
};

function SubmitButton() {
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const { watch, getFieldState } = useFormContext();
    const { modalProps } = useModal();
    const { tokenSymbol, poolBalance } = modalProps || {};
    const amount = watch(MAVaultsWithdrawalModalFields.AMOUNT);
    const { error } = getFieldState(MAVaultsWithdrawalModalFields.AMOUNT) || {};

    const getSubmitButtonText = () => {
        return walletAddress ? `Withdraw ${tokenSymbol}` : 'Connect Wallet';
    };

    const isDisabled = useMemo(() => {
        if (error) return true;
        if (!amount || Number(amount) === 0) return true;
        if (!amount && !!walletAddress) return true;
        return Number(poolBalance) < Number(amount);
    }, [poolBalance, amount, walletAddress, error]);

    return (
        <Button
            size={Button.sizes.Large}
            disabled={isDisabled}
            className='w-full font-semibold'
            type='submit'
        >
            {getSubmitButtonText()}
        </Button>
    );
}
